import { JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Button } from 'core-components/library/button'
import { Dropdown, DropdownDynamic } from 'core-components/library/dropdown'
import { Input } from 'core-components/library/inputs'
import { LoaderOverlay } from 'core-components/library/loader'
import { IModalContent, ModalDialog } from 'core-components/library/modalDialog'
import { NotificationController } from 'core-components/library/popup'
import { RichTextEditor } from 'core-components/library/richTextEditor'
import { useCurrentSitePage } from 'core-components/sitebuilder/layout/hooks/useCurrentSitePage'
import { IUser } from 'core-components/sitebuilder/models/IUser'
import { selectAppId } from 'core-components/sitebuilder/store/selectors/selectAppId'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { BaseCrud, Logic, Operator, Static } from 'core-components/types'
import { convertNavigatorToJSON } from 'core/classes/helpers/convertNavigatorToJSON'
import { guid } from 'core/classes/helpers/GuidHelper'
import { saveFilesAdapter } from 'core/classes/helpers/saveFiles'
import { CrudStoreItemCore } from 'core/classes/services/InitializationServiceCore'
import { getName } from 'core/components/shared/components/userSelectors/helpers/getName'
import { ItemRender } from 'core/components/shared/components/userSelectors/ItemRender'
import { selectSimulation } from 'core/store/selectors/selectSimulations'
import { CrudStore } from 'core/types/crudStore'

import { DEBUGGER_CRUD_NEW } from '../constants'
import { createIssue } from '../helpers/createIssue'
import { usePageTopic } from '../hooks/usePageTopic'

import { FILTER_FOR_CATEGORY_TOPIC } from './constants'
import { isNewCoreServiceAvailable } from './helpers/isNewCoreServiceAvailable'

import styles from './BugReportDialog.module.scss'

interface IProps {
  show: boolean
  onClose: () => void
  headerContent?: () => JSX.Element
}

export const BugReportDialog = (props: IProps) => {
  return (
    <ModalDialog
      size={'sm'}
      show={props.show}
      onClose={props.onClose}
      content={BugReportDialogContent}
      contentProps={{ ...props }}
    />
  )
}

const BugReportDialogContent = (props: IProps & IModalContent) => {
  const [text, setText] = useState<string>('')
  const [assignee, setAssignee] = useState<string>('')
  const [title, setTitle] = useState<string>('')
  const [users, setUsers] = useState<IUser[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isAppAdminLoading, setIsAppAdminLoading] = useState<boolean>(false)
  const [loadingOnCreate, setLoadingOnCreate] = useState<boolean>(false)
  const simulation = useSelector(selectSimulation)
  const currentSitePage = useCurrentSitePage()
  const { topic, isTopicLoading, topicUsers, topicChangeHandler } = usePageTopic(currentSitePage.id)

  const topicsCrud = CrudStore.get(DEBUGGER_CRUD_NEW.topics.key)
  const appsSource = CrudStore.get(CrudStoreItemCore.CleverApp)

  const responsibleUsersSource = useMemo(() => {
    return new BaseCrud({
      fetcher: new Static(users),
      keyField: isNewCoreServiceAvailable() ? 'id' : 'Id',
      defaultPaging: {
        page: 0,
        pageSize: 5,
      },
    })
  }, [users])

  const appId = useSelector(selectAppId)
  const user = useSelector(selectUser)

  const loaderVisible = useMemo(() => {
    return isLoading || isTopicLoading || isAppAdminLoading
  }, [isAppAdminLoading, isLoading, isTopicLoading])

  const readyToSubmit = useMemo(() => {
    return assignee && topic && text && title
  }, [assignee, text, title, topic])

  const fetchAppAdmin = useCallback(() => {
    setIsAppAdminLoading(true)
    appsSource
      .getById({ id: appId, keyField: 'CleverAppId' })
      .then((res) => {
        setAssignee(res?.ProductOwner || '')
      })
      .finally(() => setIsAppAdminLoading(false))
  }, [appId, appsSource])

  useEffect(() => {
    if (topicUsers.length) {
      setIsLoading(true)

      const fetchSource = isNewCoreServiceAvailable()
        ? CrudStore.get(CrudStoreItemCore.Users)
        : CrudStore.get(CrudStoreItemCore.UsersFromView)
      fetchSource
        .fetch({
          filter: {
            logic: Logic.AND,
            filters: [
              {
                logic: Logic.OR,
                filters: topicUsers.map((el) => ({
                  field: 'Id',
                  operator: Operator.EQUAL,
                  value: el,
                })),
              },
            ],
          },
        })
        .then((data) => {
          if (data.items.length) {
            setAssignee(isNewCoreServiceAvailable() ? data.items[0].id : data.items[0].Id)
            setUsers(data.items)
          } else {
            fetchAppAdmin()
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else if (topic) {
      fetchAppAdmin()
    }
  }, [fetchAppAdmin, appId, appsSource, topic, topicUsers])

  return (
    <div className={styles.root}>
      <LoaderOverlay show={loaderVisible} />
      <div className={styles.header}>
        <div className={styles.header__icon}>
          <i className={'kbase kbase-bug'} />
        </div>
        <div className={styles.header__title}>REPORT A PROBLEM</div>
      </div>

      {props.headerContent ? props.headerContent() : null}

      <div className={styles.content__container}>
        <Dropdown
          label={'Topic'}
          caption={'Title'}
          filter={{
            logic: Logic.AND,
            filters: [
              {
                operator: Operator.CONTAINS,
                field: 'CleverAppId',
                value: appId,
              },
              { ...FILTER_FOR_CATEGORY_TOPIC },
            ],
          }}
          source={topicsCrud}
          value={topic?.EntryId || ''}
          onChange={(value, topicInfo) => {
            topicChangeHandler(topicInfo)
            setAssignee('')
          }}
          margin={'sm'}
        />
        <DropdownDynamic
          label={'AssignedTo'}
          fieldsToSearch={['Id', 'FirstName', 'LastName']}
          renderFunction={ItemRender}
          placeholder={'Search user...'}
          calculateCaption={(item) => getName(item)}
          source={responsibleUsersSource}
          value={assignee}
          onChange={setAssignee}
          disabled={!topic || users.length <= 1}
          margin={'sm'}
        />
        <p className={styles.content__title}>Please write a short title for the problem.</p>
        <Input value={title} onChange={setTitle} placeholder="Title" />
        <p className={styles.content__title}>Please describe the problem you found.</p>
        <div className={'min-height-sm'}>
          <RichTextEditor
            onChange={(value) => {
              setText(value)
            }}
            fileUploadAdapter={saveFilesAdapter}
            placeholder={'Add content...'}
            value={text}
            toolbarVisible={false}
          />
        </div>
        <div className={styles.info}>*You can include pictures and screenshots.</div>
      </div>
      <div className={styles.btn__container}>
        <Button
          color={'primary'}
          loading={loadingOnCreate}
          disabled={!readyToSubmit}
          icon={'kbase kbase-send-alt'}
          className={styles.btn__submit}
          onClick={async () => {
            setLoadingOnCreate(true)
            const newIssue = {
              AppId: appId,
              Guid: guid(),
              Category: 'Bug',
              Status: 'New',
              Priority: 1,
              Title: title,
              Description: `${text} \nCreated from page: <a href="${window.location.href}">${window.location.href}</a>`,
              AssignedTo: assignee,
              TargetDate: null,
              DeviceInfo: convertNavigatorToJSON(simulation),
              EntryId: -1,
              Topic: topic?.Title || '',
              Tags: '',
              CreatedBy: user.email,
            }
            const result = await createIssue(newIssue)
            if (result) {
              NotificationController.showSuccess({ message: 'Your problem has been sent' })
            }
            setLoadingOnCreate(false)
            props.closeModal()
          }}
        >
          Send your problem
        </Button>
        <Button variant={'outlined'} onClick={props.closeModal}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
